import styled from '@emotion/styled'
import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMount, usePrevious } from 'react-use'

import { Button, useToast } from 'packages/common'
import { useManageConnectedDrawerState } from 'packages/common/src/modals/Drawer/hooks/useManageConnectedDrawerState'
import { useAsyncFnWithReset } from 'packages/utils/hooks'

import { Slugs, useI18n } from 'app/fieldapp/i18n'
import { createCoveragePartner } from 'app/fieldapp/store/coveragePartner/actions/createCoveragePartner'
import { ApplicationState } from 'app/fieldapp/store/store'
import { setStandardCoveragePartnerDrawerState } from 'app/fieldapp/store/ui/actions'
import { getStandardCoveragePartnerDrawerState } from 'app/fieldapp/store/ui/selectors'
import {
  fetchCoveragePartnerOptions,
  fetchCurrentCoveragePartner,
} from 'app/fieldapp/store/users/actions'
import { getCurrentCoveragePartner } from 'app/fieldapp/store/users/selectors'
import { getCoveragePartnerOptions } from 'app/fieldapp/store/users/selectors/getCoveragePartnerOptions'
import { useActiveUser } from 'app/fieldapp/utils/hooks/useActiveUser'

import { StandardCoveragePartnerDrawer } from './StandardCoveragePartnerDrawer'

const St = {
  Container: styled.div``,
}

export type StandardCoveragePartnerDrawerUsersState = {
  selectedUserId: null | string
}

export const StandardCoveragePartnerDrawerContainer: React.FC = React.memo(
  () => {
    const { t } = useI18n()
    const { showToast } = useToast()
    const drawerManager = useManageConnectedDrawerState<ApplicationState>({
      dispatchableToggleDrawerAction: setStandardCoveragePartnerDrawerState,
      selector: getStandardCoveragePartnerDrawerState,
    })

    const coveragePartnerOptions = useSelector(getCoveragePartnerOptions)

    const currentPartner = useSelector(getCurrentCoveragePartner)

    const [currentPartnerState, setCurrentPartnerState] =
      React.useState<StandardCoveragePartnerDrawerUsersState>({
        selectedUserId: currentPartner?.id || null,
      })

    const prevCurrentUserId = usePrevious(currentPartner?.id)

    useEffect(() => {
      if (!prevCurrentUserId && currentPartner?.id) {
        setCurrentPartnerState({
          selectedUserId: currentPartner?.id || null,
        })
      }
    }, [currentPartner?.id, prevCurrentUserId])

    const selectedUser = coveragePartnerOptions.find(
      ({ id }) => id === currentPartnerState.selectedUserId,
    )

    const setSelectedUser = selectedUser => {
      setCurrentPartnerState({ selectedUserId: selectedUser.id })
    }

    const { user } = useActiveUser()

    const dispatch = useDispatch()

    const [fetchOptionsState, fetchCoveragePartnerOptionsFn] =
      useAsyncFnWithReset(async () => {
        return dispatch(fetchCoveragePartnerOptions())
      }, [dispatch])

    const [fetchExistingPartnerState, fetchExistingPartnerFn] =
      useAsyncFnWithReset(async () => {
        return dispatch(fetchCurrentCoveragePartner())
      }, [dispatch])

    const [
      createCoveragePartnerState,
      createCoveragePartnerFn,
      createCoveragePartnerResetFn,
    ] = useAsyncFnWithReset(async () => {
      if (!user?.id || !selectedUser?.id) return
      return dispatch(
        createCoveragePartner(
          {
            coveredUserId: user.id,
            coveringUserId: selectedUser.id,
          },
          {
            onSuccess: () => {
              showToast({
                message: t(Slugs.createCoveragePartnerSuccess),
                toastType: 'success',
              })

              drawerManager.closeDrawer()
            },
          },
        ),
      )
    }, [dispatch, drawerManager, selectedUser?.id, showToast, t, user?.id])

    useMount(() => {
      fetchCoveragePartnerOptionsFn()
      fetchExistingPartnerFn()
    })

    const hasUserChanged = React.useMemo(() => {
      return selectedUser?.id !== currentPartner?.id
    }, [currentPartner?.id, selectedUser])

    const onSubmit = () => {
      createCoveragePartnerFn()
    }

    const errorState = useMemo(
      () => ({
        createCoveragePartnerError: createCoveragePartnerState.error,
        fetchCoveragePartnerOptionsError: fetchOptionsState.error,
        fetchCurrentCoveragePartnerError: fetchExistingPartnerState.error,
      }),
      [
        fetchOptionsState,
        fetchExistingPartnerState,
        createCoveragePartnerState,
      ],
    )

    const isCreateLoading = createCoveragePartnerState.loading

    return (
      <St.Container>
        <Button
          block={true}
          buttonType={'utility'}
          isLoading={fetchOptionsState.loading}
          onClick={() => drawerManager.openDrawer()}
        >
          {t(Slugs.editCoveragePartner)}
        </Button>
        <StandardCoveragePartnerDrawer
          {...drawerManager}
          errors={errorState}
          hasUserChanged={hasUserChanged}
          isLoading={isCreateLoading}
          onSubmit={onSubmit}
          reset={createCoveragePartnerResetFn}
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
          coveragePartnerOptions={coveragePartnerOptions}
        />
      </St.Container>
    )
  },
)
